import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useStyles } from '../styles/CreditCardComparisonStyle'

const MobileCardDetails = ({ card, features, benefits }) => {
  const classes = useStyles()
  const [activeTab, setActiveTab] = useState('features')
  const [expandedSections, setExpandedSections] = useState({})

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  // Check if a section is expanded
  const isSectionExpanded = (sectionId) => {
    return expandedSections[sectionId] === true
  }

  // Function to render a formatted value
  const renderFormattedValue = (value, title) => {
    if (typeof value === 'boolean') {
      if (value) {
        return (
          <Typography className={`${classes.featureValue} ${classes.featureValuePositive}`}>
            <CheckCircleIcon fontSize="small" className={classes.checkIcon} /> Yes
          </Typography>
        )
      } else {
        return (
          <Typography className={`${classes.featureValue} ${classes.featureValueNegative}`}>
            <CancelIcon fontSize="small" style={{ marginRight: '6px', fontSize: '16px' }} /> No
          </Typography>
        )
      }
    }

    if (Array.isArray(value)) {
      return (
        <Box className={classes.featureValue}>
          {value.map((item, idx) => (
            <div key={idx} className={classes.bulletItem}>
              <span className={classes.bulletPoint}>•</span>
              <span>{item?.label || 'N/A'}</span>
            </div>
          ))}
        </Box>
      )
    }
    
    // Format based on title content
    if (title.toLowerCase().includes('rate') || title.toLowerCase().includes('apr')) {
      return (
        <Typography className={classes.featureValue}>
          {typeof value === 'string' && value.includes('%') ? 
            <strong>{value}</strong> : value || 'N/A'}
        </Typography>
      )
    }
    
    return (
      <Typography className={classes.featureValue}>
        {value || 'N/A'}
      </Typography>
    )
  }

  // Group features by categories
  const primaryFeatures = features.slice(0, 3) // Most important features
  const secondaryFeatures = features.slice(3)

  return (
    <Box className={classes.mobileComparisonContainer}>
      <Typography variant="h6" className={classes.cardTitle}>
        {card?.name || 'Credit Card'}
      </Typography>

      {/* Tabbed navigation */}
      <Box display="flex" className={classes.tabsContainer}>
        <Box 
          className={`${classes.sectionTab} ${activeTab === 'features' ? classes.activeTab : ''}`}
          onClick={() => setActiveTab('features')}
        >
          Features
        </Box>
        <Box 
          className={`${classes.sectionTab} ${activeTab === 'benefits' ? classes.activeTab : ''}`}
          onClick={() => setActiveTab('benefits')}
        >
          Benefits
        </Box>
      </Box>

      {/* Features Tab Content */}
      {activeTab === 'features' && (
        <>
          {/* Highlight key features */}
          {primaryFeatures.map((feature, idx) => (
            <Box key={idx} className={classes.highlightedFeature}>
              <Box className={classes.highlightedFeatureContent}>
                <Typography className={classes.featureTitle}>
                  {feature.title}:
                </Typography>
                {renderFormattedValue(feature.value, feature.title)}
              </Box>
            </Box>
          ))}
          
          {/* Collapsible sections for secondary features */}
          <Box className={classes.comparisonSection}>
            <Typography 
              className={classes.sectionTitle}
              onClick={() => toggleSection('more_features')}
            >
              More Features
              <ExpandMoreIcon 
                className={`${classes.sectionIcon} ${isSectionExpanded('more_features') ? classes.rotatedIcon : ''}`} 
              />
            </Typography>
            
            <Box 
              className={`${classes.collapsibleSection} ${isSectionExpanded('more_features') ? classes.expandedSection : ''}`}
            >
              {secondaryFeatures.map((feature, idx) => (
                <Box key={idx} className={classes.comparisonRow}>
                  <Typography className={classes.featureTitle}>
                    {feature.title}:
                  </Typography>
                  {renderFormattedValue(feature.value, feature.title)}
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* Benefits Tab Content */}
      {activeTab === 'benefits' && (
        <Box className={classes.comparisonSection}>
          {benefits.map((benefit, idx) => (
            <Box key={idx} className={classes.comparisonRow}>
              <Typography className={classes.featureTitle}>
                {benefit.title}:
              </Typography>
              {renderFormattedValue(benefit.value, benefit.title)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default MobileCardDetails
